import { Group, Input, Paper, Radio, Select, Stack, Switch, Text, ThemeIcon } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconBuilding, IconQrcode, IconUser } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useLoadTemplates } from 'modules/Settings/Templates/api';
import { useEffect } from 'react';
import type { z } from 'zod/v4';

import {
  InvoiceSendingOptionsSchema,
  type UpdateInvoiceSendingOptionsVariables,
  useLoadInvoiceSendingOptions,
  useUpdateInvoiceSendingOptions,
} from '../api';

type SendingOptionsFormValues = z.infer<typeof InvoiceSendingOptionsSchema>;

const INITIAL_VALUES: SendingOptionsFormValues = {
  generateInvoiceInDebtorLocale: false,
  defaultInvoiceTemplateId: null,
  defaultCreditNoteTemplateId: null,
  displayQRCode: true,
};

// The locale choice is a fixed two-option toggle: `false` = the company's own
// language, `true` = the client's (debtor's) language.
const LOCALE_OPTIONS = [
  { value: 'false', labelKey: i18nKeys.COMPANY_LOCALE, Icon: IconBuilding },
  { value: 'true', labelKey: i18nKeys.DEBTOR_LOCALE, Icon: IconUser },
] as const;

export const SendingOptionsCard = () => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const canUseOnlinePayment = company.package.can_use_online_payment;

  const { invoiceSendingOptions } = useLoadInvoiceSendingOptions();
  const { updateInvoiceSendingOptions, isUpdateInvoiceSendingOptionsLoading } =
    useUpdateInvoiceSendingOptions();

  const { templates } = useLoadTemplates({ templateType: 'recovery' });
  const invoiceTemplates = templates.filter(({ stepType }) => stepType === 'invoice');
  const creditNoteTemplates = templates.filter(({ stepType }) => stepType === 'credit_note');

  const form = useForm<SendingOptionsFormValues>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(InvoiceSendingOptionsSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded options into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!invoiceSendingOptions) return;

    form.setInitialValues(invoiceSendingOptions);
    form.setValues(invoiceSendingOptions);
  }, [invoiceSendingOptions]);

  const handleSave = () => {
    const values = form.getValues();
    const payload: UpdateInvoiceSendingOptionsVariables = {};
    if (form.isDirty('generateInvoiceInDebtorLocale'))
      payload.generateInvoiceInDebtorLocale = values.generateInvoiceInDebtorLocale;
    if (form.isDirty('defaultInvoiceTemplateId'))
      payload.defaultInvoiceTemplateId = values.defaultInvoiceTemplateId;
    if (form.isDirty('defaultCreditNoteTemplateId'))
      payload.defaultCreditNoteTemplateId = values.defaultCreditNoteTemplateId;
    if (form.isDirty('displayQRCode')) payload.displayQRCode = values.displayQRCode;

    updateInvoiceSendingOptions(payload, { onSuccess: () => form.resetDirty() });
  };

  const { generateInvoiceInDebtorLocale, displayQRCode } = form.getValues();

  return (
    <Panel
      title={t(i18nKeys.SENDING_SETTINGS)}
      withBorder
      fullHeight
      pinFooter
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateInvoiceSendingOptionsLoading}
          hideIcon
        />
      }
    >
      <Stack my="md">
        <Input.Wrapper label={t(i18nKeys.DOCUMENTS_LOCALE)}>
          <Radio.Group
            value={String(generateInvoiceInDebtorLocale)}
            onChange={next => form.setFieldValue('generateInvoiceInDebtorLocale', next === 'true')}
          >
            <Group grow gap="sm">
              {LOCALE_OPTIONS.map(({ value, labelKey, Icon }) => (
                <Radio.Card key={value} value={value} p="sm" radius="md">
                  <Group gap="sm" wrap="nowrap" align="center">
                    <Radio.Indicator />
                    <ThemeIcon variant="light" size="lg" radius="md">
                      <Icon size={18} />
                    </ThemeIcon>
                    <Text size="sm">{t(labelKey)}</Text>
                  </Group>
                </Radio.Card>
              ))}
            </Group>
          </Radio.Group>
        </Input.Wrapper>
        <Select
          label={t(i18nKeys.DEFAULT_INVOICE_TEMPLATE)}
          allowDeselect={false}
          data={invoiceTemplates.map(({ id, name }) => ({ value: id, label: name }))}
          {...form.getInputProps('defaultInvoiceTemplateId')}
        />
        <Select
          label={t(i18nKeys.DEFAULT_CN_TEMPLATE)}
          allowDeselect={false}
          data={creditNoteTemplates.map(({ id, name }) => ({ value: id, label: name }))}
          {...form.getInputProps('defaultCreditNoteTemplateId')}
        />
        <Paper withBorder radius="md" p="sm" mt="xs">
          <Switch
            w="100%"
            labelPosition="left"
            disabled={!canUseOnlinePayment}
            checked={canUseOnlinePayment && displayQRCode}
            onChange={event => form.setFieldValue('displayQRCode', event.currentTarget.checked)}
            styles={{ body: { alignItems: 'center' }, labelWrapper: { flex: 1 } }}
            label={
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" size="lg" radius="md">
                  <IconQrcode size={18} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text size="sm" fw={500}>
                    {t(i18nKeys.DISPLAY_QR_CODE)}
                  </Text>
                  {!canUseOnlinePayment && (
                    <Text size="xs" c="dimmed">
                      {t(i18nKeys.NOT_INCLUDED)}
                    </Text>
                  )}
                </Stack>
              </Group>
            }
          />
        </Paper>
      </Stack>
    </Panel>
  );
};
